import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../../app/providers/LanguageProvider';
import VisuallyHidden from '../../../components/primitives/VisuallyHidden';
// Deep-import SectionEyebrow (a pure HTML/CSS component) — the
// `components/motion` barrel re-exports ScrollPinnedSection (gsap) and
// motion-using primitives, which would drag gsap + motion-dom (~250 KB raw)
// into the main bundle since HeroSection is now eager.
import SectionEyebrow from '../../../components/motion/SectionEyebrow';
import { HeroStaticFallback } from '../three/HeroCanvas';
import { useReducedMotion } from '../../../styles/motion/hooks';
import './HeroSection.css';

// All hero elements are now plain DOM with CSS-only entrance animations.
// motion.X was causing a hydration mismatch on lazy chunk load (prerendered
// HTML had final-state inline styles like `style="opacity:1"`, but motion's
// `initial={opacity:0}` re-rendered them to 0 then animated, which re-fired
// LCP and cost ~2.5s on mobile). CSS-only animations animate `transform`
// only — opacity stays at 1 throughout, so Lighthouse captures LCP at the
// prerendered HTML moment.

const HeroSection = () => {
  const { t } = useTranslation();
  const { language, direction } = useLanguage();
  const reduced = useReducedMotion();
  const isArabic = language === 'ar';

  // Hero Uqda engagement state: when the visitor hovers/focuses the primary
  // CTA, the mascot snaps to a "discovering" pose with a halo boost — a
  // visual nudge that says "yes, click this." Reset to idle when interest
  // moves away. Keyboard users get it via focus/blur.
  const [heroEngaged, setHeroEngaged] = useState(false);
  const engage = useCallback(() => setHeroEngaged(true), []);
  const disengage = useCallback(() => setHeroEngaged(false), []);

  const line2 = t('landing.hero.headlineLine2');
  const companion = t('landing.hero.headlineArabic');

  const scrollToTry = useCallback((event) => {
    if (event) event.preventDefault();
    const el = document.getElementById('try-it-live');
    if (!el) return;
    el.scrollIntoView({
      behavior: reduced ? 'auto' : 'smooth',
      block: 'start',
    });
    // Move focus into the section for keyboard users without forcing a visible
    // outline on the section element itself.
    const focusTarget = el.querySelector('[data-tryitlive-focus]') || el;
    if (focusTarget && typeof focusTarget.focus === 'function') {
      focusTarget.focus({ preventScroll: true });
    }
  }, [reduced]);

  return (
    <section
      className="hero-section hero-section--light"
      aria-labelledby="hero-heading"
      dir={direction}
    >
      <div className="hero-section__inner">
        <div className="hero-section__content">
          {/* Eyebrow — Instrument Serif italic, low-key */}
          <SectionEyebrow
            as="span"
            serif
            className="hero-section__eyebrow"
          >
            {t('landing.hero.eyebrow')}
          </SectionEyebrow>

          {/*
            LCP-eligible text — H1 line 1/2, companion, and subhead — is plain
            DOM with CSS-only `translateY` entrance animations. They paint at
            opacity:1 immediately (so Lighthouse captures LCP at first paint
            instead of waiting for motion's opacity:0→1 to resolve, which on
            mobile was costing ~4-5s of "render delay"). The polish is preserved
            via .hero-entrance-* keyframes in HeroSection.css.
          */}
          <h1 id="hero-heading" className="hero-section__headline">
            <span className="hero-section__headline-primary">
              {t('landing.hero.headlineLine1')}
              {line2 ? (
                <>
                  <br />
                  <span className="hero-section__headline-accent section-landing__accent hero-entrance-line2">
                    {line2}
                  </span>
                </>
              ) : null}
            </span>
            {companion ? (
              <span
                className="hero-section__headline-companion hero-entrance-companion"
                lang={isArabic ? 'en' : 'ar'}
                dir={isArabic ? 'ltr' : 'rtl'}
              >
                {companion}
              </span>
            ) : null}
          </h1>

          {/* AI-citation target — answer-shaped opener, lifted into VisuallyHidden
            lede so it stays indexable for crawlers/AT but no longer competes
            with the new subhead visually. */}
          <VisuallyHidden as="p">
            {t('landing.hero.answerOpener')}
          </VisuallyHidden>

          <p className="hero-section__subhead hero-entrance-subhead">
            {t('landing.hero.subheadV3')}
          </p>

          <div className="hero-section__cta-stack">
            <a
              href="#try-it-live"
              className="hero-cta hero-cta--primary hero-entrance-cta-primary"
              onClick={scrollToTry}
              onMouseEnter={engage}
              onMouseLeave={disengage}
              onFocus={engage}
              onBlur={disengage}
            >
              {t('landing.hero.ctaPrimary')}
            </a>
            <a
              href="#try-it-live"
              className="hero-section__secondary-link hero-entrance-cta-secondary"
              onClick={scrollToTry}
            >
              {t('landing.hero.ctaSecondaryText')}
            </a>
          </div>
        </div>

        <div className="hero-section__visual" aria-hidden="false">
          <VisuallyHidden>
            {t(
              'landing.hero.canvasDescription',
              'Decorative animation: a constellation of knowledge anchors orbiting a memory core.',
            )}
          </VisuallyHidden>
          {/* HeroStaticFallback is the eager LCP-eligible mascot.
              The lazy <HeroCanvas /> (3D particle field) was removed for the
              white-hero variant — additive blending made its emerald points
              invisible on white, AND its own HeroStaticFallback fallback
              was rendering a second mascot underneath the one above. */}
          <HeroStaticFallback engaged={heroEngaged} />
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
